import React, { useEffect, useRef, useMemo, useCallback } from "react";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import {
  TooltipComponent,
  GridComponent,
  MarkLineComponent,
  LegendComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { formatNumberWithIntl } from "@wavvest/shared-utils";
import { HomePriceProjection } from "@/common/widgets/portfolio/util";

echarts.use([
  LineChart,
  TooltipComponent,
  GridComponent,
  MarkLineComponent,
  LegendComponent,
  CanvasRenderer,
]);

interface HomePriceChartProps {
  data: HomePriceProjection[];
  width?: number;
  height?: number;
  themeColors?: {
    backgroundColor: string;
    textColor: string;
    lineColor: string;
    areaColors: {
      start: string;
      end: string;
    };
  };
  indicator?: {
    x: number;
    y: number;
    value: number;
    name: string;
  } | null;
  setIndicator: (
    indicator: {
      x: number;
      y: number;
      value: number;
      name: string;
    } | null,
  ) => void;
}

export const HomePriceChart: React.FC<HomePriceChartProps> = ({
  data,
  width,
  height,
  themeColors = {
    backgroundColor: "#222222",
    textColor: "#ABABAB",
    lineColor: "#AD56D6",
    areaColors: {
      start: "#BDA1C9",
      end: "#7B3998",
    },
  },
  indicator,
  setIndicator,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 设置默认的 indicator
  useEffect(() => {
    if (data.length > 0 && !indicator) {
      setIndicator({
        x: 0,
        y: data[0].projectionValue,
        name: data[0].year.toString(),
        value: data[0].projectionValue,
      });
    }
  }, [data, indicator, setIndicator]);

  const option = useMemo(
    () => ({
      backgroundColor: themeColors.backgroundColor,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          label: {
            backgroundColor: themeColors.backgroundColor,
          },
        },
        formatter: function (params: any) {
          return `${params[0].marker} ${params[0].seriesName}: ${formatNumberWithIntl({ num: params[0].value })}`;
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.map((item) => item.year),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: themeColors.textColor } },
        axisLabel: { color: themeColors.textColor },
      },
      yAxis: {
        type: "value",
        axisLine: { lineStyle: { color: themeColors.textColor } },
        axisLabel: {
          color: themeColors.textColor,
          formatter: (value: number) => formatNumberWithIntl({ num: value }),
        },
        splitLine: {
          lineStyle: {
            color: "#FFFFFF33",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Estate Value",
          type: "line",
          smooth: true,
          symbol: "none",
          lineStyle: {
            color: themeColors.lineColor,
            width: 2,
          },
          itemStyle: {
            color: themeColors.lineColor,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0.5,
                color: themeColors.areaColors.start,
              },
              {
                offset: 1,
                color: themeColors.areaColors.end,
              },
            ]),
          },
          data: data.map((item) => item.projectionValue),
          markLine: indicator
            ? {
                data: [
                  {
                    xAxis: indicator.name,
                  },
                ],
                animation: false,
                symbol: "none",
                lineStyle: {
                  color: themeColors.textColor,
                  type: [5, 10],
                },
                label: {
                  show: false,
                },
              }
            : undefined,
        },
      ],
    }),
    [data, themeColors, indicator],
  );

  const handleChartClick = useCallback(
    (event: any) => {
      if (!chartInstance.current) return;

      const pointInPixel = [event.offsetX, event.offsetY];
      const pointInGrid = chartInstance.current.convertFromPixel(
        { seriesIndex: 0 },
        pointInPixel,
      );

      if (pointInGrid) {
        const [xValue, yValue] = pointInGrid;
        const index = Math.round(xValue);

        if (index >= 0 && index < data.length) {
          setIndicator({
            x: xValue,
            y: yValue,
            name: data[index].year.toString(),
            value: data[index].projectionValue,
          });
        }
      }
    },
    [data, setIndicator],
  );

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    try {
      chartInstance.current.setOption(option);

      const zr = chartInstance.current.getZr();
      zr.off("click");
      zr.on("click", handleChartClick);
    } catch (error) {
      console.error("Error setting chart options:", error);
    }

    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [option, handleChartClick]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  const indicatorContent = useMemo(() => {
    if (!indicator) return null;

    return (
      <>
        <div className="selected" style={styles.infoRow}>
          <div className="name">Year</div>
          <div className="value">{indicator.name}</div>
        </div>
        <div className="selected" style={styles.infoRow}>
          <div className="name" style={styles.item}>
            <div
              style={{
                ...styles.colorDot,
                backgroundColor: themeColors.lineColor,
              }}
            />
            <span>Home Price</span>
          </div>
          <div className="value">
            {formatNumberWithIntl({ num: indicator.value })}
          </div>
        </div>
      </>
    );
  }, [indicator, themeColors.lineColor]);

  return (
    <div className="chart">
      <div className="title">
        <span className="title-name">Your home price projection</span>
      </div>
      <div
        ref={chartRef}
        style={{ width: width ? width : "100%", height: height ? height : 400 }}
      />
      {indicatorContent}
    </div>
  );
};

const styles = {
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  colorDot: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
  },
} as const;

export default HomePriceChart;
